// ── CLIENT CREDITS SECTION ────────────────────────────────────────────────────
// Per-client deposit credit ledger. Mounts under Client Invoices → Credits.
// Mirrors the RetainersSection pattern: client list with balances, click in
// for the full ledger view (date, type, amount, reference, running balance).
//
// Credits accumulate when a client cancels an item with a deposit paid
// (CancelItemModal writes a + entry). Credits draw down when applied to
// another item, refunded as cash, or written off.

import React, { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { calcClientCreditBalance, clientCreditEntriesWithRunningBalance, fmtMoney } from '../utils';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

var btnGhost = { height: 32, padding: '0 14px', borderRadius: 18, border: '0.5px solid #E0E0DC', background: '#FFFFFF', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#111', fontFamily: 'inherit' };
var TYPE_LABEL = {
  cancelled_deposit:          'Cancelled Item Credit',
  co_credit:                  'Change Order Credit',
  overpayment:                'Overpayment',
  applied_to_item:            'Applied to Item',
  applied_to_invoice:         'Applied to Invoice',
  applied_to_billing_summary: 'Applied to Billing Summary',
  refund:                     'Cash Refund',
};
var TYPE_COLOR = {
  cancelled_deposit:          '#1A5C38',
  co_credit:                  '#1A5C38',
  overpayment:                '#1A5C38',
  applied_to_item:            '#888880',
  applied_to_invoice:         '#888880',
  applied_to_billing_summary: '#888880',
  refund:                     '#C8322A',
};

export default function ClientCreditsSection() {
  var ctx = useAppContext();
  var clients = ctx.clients || [];
  var items = ctx.items || [];
  var clientCreditLedger = ctx.clientCreditLedger || [];

  var sps = useState('');               var selClientId = sps[0];   var setSelClientId = sps[1];
  var setClientCreditLedger = ctx.setClientCreditLedger;

  var clientBalances = useMemo(function() {
    return clients.map(function(c) {
      // String coercion on both sides — clients.id from Date.now() is a
      // number; e.clientId is string (text column). rowToClientCredit
      // already coerces, but kept defensive here for clarity at the
      // filter site (mirrors the pattern in BankDepositModal +
      // ProposalsTab2 + QuickBooksTab where the same mismatch lives).
      var cid = String(c.id);
      var bal = calcClientCreditBalance(clientCreditLedger, cid);
      var entries = clientCreditLedger.filter(function(e) { return String(e.clientId || '') === cid; });
      // Most recent entry by entryDate (real-world date the credit
      // represents, not the DB write timestamp — a backdated SQL insert
      // should still rank by the date it semantically belongs to).
      // String compare works because entryDate is ISO YYYY-MM-DD.
      var latest = entries.reduce(function(best, e) {
        if (!best) return e;
        var bd = String(best.entryDate || '');
        var ed = String(e.entryDate || '');
        return ed > bd ? e : best;
      }, null);
      return { client: c, balance: bal, count: entries.length, latest: latest };
    }).filter(function(row) { return row.count > 0; });
  }, [clients, clientCreditLedger]);

  var selClient = clients.find(function(c) { return c.id === selClientId; });
  var ledgerForClient = useMemo(function() {
    if (!selClientId) return [];
    return clientCreditEntriesWithRunningBalance(clientCreditLedger, selClientId).slice().reverse();
  }, [clientCreditLedger, selClientId]);

  function itemLabel(itemId) {
    if (!itemId) return '';
    var it = items.find(function(x) { return x.id === itemId; });
    if (!it) return '';
    return (it.itemNumber || '') + ' · ' + (it.item || it.productName || '');
  }

  // ── Client list view ──
  if (!selClientId) {
    return (
      <div style={{ padding: '20px 18px', maxWidth: 900 }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Client Credits</div>
          <div style={{ fontSize: 12, color: '#888', lineHeight: 1.5 }}>
            Available client credit balances from cancelled items. Apply when generating the next deposit invoice or refund as cash.
          </div>
        </div>

        {clientBalances.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 8 }}>
            No client credits on file. When an item with a paid deposit is cancelled, the credit will land here.
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F7F7F5', borderBottom: '0.5px solid #E0E0DC' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Client</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Entries</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Latest Entry</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Reference</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Available Credit</th>
                </tr>
              </thead>
              <tbody>
                {clientBalances.map(function(row) {
                  return (
                    <tr key={row.client.id}
                      onClick={function() { setSelClientId(row.client.id); }}
                      style={{ cursor: 'pointer', borderBottom: '0.5px solid #EFEFEC' }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                      <td style={{ padding: '12px 14px', fontWeight: 500, color: '#111' }}>{row.client.name || row.client.id}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', color: '#888' }}>{row.count}</td>
                      <td style={{ padding: '12px 14px', color: '#555', whiteSpace: 'nowrap' }}>
                        {row.latest ? fmtDate(row.latest.entryDate) : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#555', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={row.latest && row.latest.reference ? row.latest.reference : ''}>
                        {row.latest && row.latest.reference ? row.latest.reference : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 500, color: row.balance > 0 ? '#1A5C38' : '#111' }}>{fmtMoney(row.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── Single-client ledger view ──
  var balance = calcClientCreditBalance(clientCreditLedger, selClientId);

  return (
    <div style={{ padding: '20px 18px', maxWidth: 1000 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={function() { setSelClientId(''); }} style={btnGhost}>← All credits</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>{selClient ? (selClient.name || selClient.id) : selClientId}</div>
          <div style={{ fontSize: 11, color: '#888' }}>Credit ledger</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Available</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: balance > 0 ? '#1A5C38' : '#111' }}>{fmtMoney(balance)}</div>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
        {ledgerForClient.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic' }}>No entries.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F7F7F5', borderBottom: '0.5px solid #E0E0DC' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Date</th>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Detail</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amount</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledgerForClient.map(function(e) {
                return (
                  <tr key={e.id} style={{ borderBottom: '0.5px solid #EFEFEC' }}>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{fmtDate(e.entryDate)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLOR[e.entryType] || '#888' }} />
                        <span style={{ color: '#111' }}>{TYPE_LABEL[e.entryType] || e.entryType}</span>
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#555', fontSize: 11 }}>
                      <div>{e.reference || '—'}</div>
                      {e.notes && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{e.notes}</div>}
                      {e.targetItemId && <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>→ {itemLabel(e.targetItemId)}</div>}
                      {e.sourceItemId && !e.targetItemId && <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>from {itemLabel(e.sourceItemId)}</div>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500, color: e.amount < 0 ? '#C8322A' : '#1A5C38' }}>
                      {fmtMoney(e.amount)}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#555' }}>
                      {fmtMoney(e.runningBalance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
